import React, { forwardRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { OptimizedImage } from '../optimized-image/optimized-image';
import { audioManager } from 'lib/audio/audio-manager';
import { AUDIO_TYPES } from 'lib/audio/audio.types';

import blick from 'assets/animation/buttons/blick.png?format=webp;avif&as=picture';
import glare from 'assets/animation/buttons/glare.png?format=webp;avif&as=picture';
import LoadingSpinner from 'assets/animation/buttons/spinner-brown.svg?react';

interface IPrimaryButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  text?: string;
  svg?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  className?: string;
  innerClassName?: string;
  isAnimated?: boolean;
  buttonType?: 'primary' | 'return' | 'accept';
  isMobile?: boolean;
  textClassName?: string;
}

const buttonOptions = {
  primary: {
    buttonStyles: 'bg-primary-border',
    innerWrapperStyles:
      'bg-primary-btn shadow-primary-btn text-[#6C3C09] hover:bg-primary-btn-hover active:shadow-primary-btn-active',
    innerLoadingStyles: 'bg-primary-btn shadow-primary-btn text-[#6C3C09]',
  },
  return: {
    buttonStyles: 'bg-return-border',
    innerWrapperStyles:
      'bg-return-btn shadow-return-btn text-[#182948] active:shadow-return-btn-active',
    innerLoadingStyles: 'bg-return-btn shadow-return-btn text-[#182948]',
  },
  accept: {
    buttonStyles: 'bg-accept-border',
    innerWrapperStyles:
      'bg-accept-btn shadow-accept-btn text-[#184839] active:shadow-accept-btn-active',
    innerLoadingStyles: 'bg-accept-btn shadow-accept-btn text-[#184839]',
  },
};

const PrimaryButton = forwardRef<HTMLButtonElement, IPrimaryButtonProps>((props, ref) => {
  const [isHovered, setIsHovered] = useState(false);
  const {
    className,
    innerClassName,
    text,
    svg,
    onClick,
    disabled,
    isLoading,
    isAnimated = false,
    buttonType = 'primary',
    isMobile = false,
    textClassName,
    ...rest
  } = props;
  const { t } = useTranslation();

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      if (!disabled && !isLoading) {
        audioManager.playSound(AUDIO_TYPES.CLICK);
        onClick();
      }
    },
    [onClick, disabled, isLoading],
  );

  return isLoading ? (
    <button
      ref={ref}
      className={`min-h-[50px] rounded-xl p-0.5 outline-none ${className ? className : ''} ${
        disabled ? 'bg-[#322E66]' : buttonOptions[buttonType].buttonStyles
      }`}
      {...rest}
    >
      <div
        className={`relative inset-0 overflow-hidden rounded-[10px] px-5 py-[11px] 2xl:px-4 ${buttonOptions[buttonType].innerLoadingStyles}`}
      >
        <div className="flex items-center justify-center">
          <LoadingSpinner className="h-6 w-6 animate-spin" />
        </div>
        {!disabled && (
          <>
            <OptimizedImage
              imageData={blick}
              alt="wide rectangle"
              className="absolute left-[-10%] top-[-30px] max-h-[136px] max-w-[144px] transition-all duration-300 ease-in-out"
            />
            <OptimizedImage
              imageData={glare}
              alt="glare effect"
              className={`absolute left-[-100%] top-[-40px] h-[130px] w-24 transition-all duration-500 ease-in-out group-hover:left-[100%] ${
                isAnimated && !isHovered ? 'animate-glare' : ''
              }`}
            />
          </>
        )}
      </div>
    </button>
  ) : (
    <button
      ref={ref}
      className={`min-h-[50px] rounded-xl p-0.5 outline-none sm:text-[22px] ${className ? className : ''} ${
        disabled
          ? 'bg-[#322E66] text-[12px] sm:text-[18px]'
          : buttonOptions[buttonType].buttonStyles
      }`}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={disabled}
      {...rest}
    >
      <div
        className={`group relative inset-0 z-10 flex h-full items-center justify-center overflow-hidden rounded-[10px] px-4 py-[11px] font-semibold sm:py-3 sm:leading-6 ${
          disabled
            ? 'bg-[#100E34] text-[#322E66] shadow-primary-btn-disabled sm:text-[18px]'
            : `${buttonOptions[buttonType].innerWrapperStyles} transition-all duration-300 ease-in-out`
        } ${innerClassName ? innerClassName : ''}`}
      >
        <div
          className={`relative z-40 flex items-center justify-center gap-1 transition-all duration-300 ease-out xs:tracking-normal ${!disabled ? 'group-active:scale-90' : ''} ${!disabled && isMobile ? 'text-[14px]' : ''} `}
        >
          {!disabled && svg}
          <span className={`whitespace-nowrap ${textClassName ? textClassName : ''}`}>
            {t(text ?? '')}
          </span>
        </div>

        {!disabled && (
          <>
            <OptimizedImage
              imageData={blick}
              alt="wide rectangle"
              className="absolute left-[-10%] top-[-30px] z-30 max-h-[136px] max-w-[144px] transition-all duration-300 ease-in-out group-hover:left-custom-left"
            />
            <OptimizedImage
              imageData={glare}
              alt="glare effect"
              className={`absolute left-[-100%] top-[-40px] z-30 h-[130px] w-[96px] transition-all duration-500 ease-in-out group-hover:left-[100%] ${
                isAnimated && !isHovered ? 'animate-glare' : ''
              }`}
            />
          </>
        )}
      </div>
    </button>
  );
});

export default PrimaryButton;
